import { isEmpty, isEqual, isNil, omit } from 'lodash'

import { formatCallFilters } from '@/reducers/callSearch/callSearch.actions'
import { initialState as callSearchInitialState } from '@/reducers/callSearch/callSearch.redux'
import { minCallDurationOptions } from '@/utils/constants'

/** The filter state keys holding realtime event selections. */
const EVENT_SECTIONS = ['checklist', 'deck', 'postcall', 'notifications']

/**
 * Filters the Trends results endpoint cannot apply, stripped before the request.
 *
 * Keyword search runs through Elasticsearch in reporting, and the tracked
 * insight rollup is a SQL aggregate over the insight's own results — there is
 * no call list to re-rank. The AI agent filters are scoped to voice agent
 * calls, which tracked insights do not cover.
 *
 * The endpoint rejects unknown fields outright (422), so leaving any of these
 * in would fail the request rather than quietly ignoring them. That is
 * deliberate: the Trends filter bar simply never offers them.
 */
const UNSUPPORTED_KEYS = [
  'keywords',
  'keyword_search_operator',
  'keyword_side_filter',
  'ai_agent_outcomes',
  'ai_agent_disconnection_reasons',
  'is_ai_call',
  // paging concerns for the call list, meaningless for an aggregate
  'limit',
  'call_ids',
  // An insight is generated from one channel and already carries it. The board
  // is a list of insights rather than a list of calls, so imposing a channel
  // across all of them would query, say, an email insight against the voice
  // tables and return nothing - formatCallFilters always emits this from the
  // channel selection, default included.
  'call_types',
]

/**
 * Build the Trends results request body from Call Explorer filter state.
 *
 * Reuses `formatCallFilters` so the two surfaces map filters identically —
 * a tag or event filter means the same thing in Trends as in Call Explorer,
 * and only has to be fixed in one place.
 *
 * @param {object} filters - Call Explorer shaped filter state
 * @param {object} playbookData - playbook config used to resolve event filters
 * @param {string} startTime - ISO start of the results window
 * @param {string} endTime - ISO end of the results window
 */
export const formatTrendsFilters = (filters, playbookData, startTime, endTime) => {
  const body = omit(formatCallFilters(filters, playbookData), UNSUPPORTED_KEYS)

  // The rollup window is the endpoint's own parameter pair, shared with the GET
  // route, so the call-list naming does not carry over.
  delete body.start_date
  delete body.end_date
  delete body.organization_id

  // formatCallDuration turns an unset duration into '1' - Call Explorer's
  // default, not the board's. The board opens with no floor, so an unset
  // duration must not travel as a one-minute one and quietly drop short calls.
  // maxCallDuration is likewise defaulted to 0 rather than omitted.
  const unset = (value) => isNil(value) || value === ''
  if (unset(filters.callDuration)) {
    delete body.call_duration
  }
  if (unset(filters.maxCallDuration)) {
    delete body.max_call_duration
  }

  return { ...body, start_time: startTime, end_time: endTime }
}

/**
 * Human-readable description of each applied board filter.
 *
 * Shared by the filter pill row and the empty state so the two can never
 * describe the same scope differently. Filters with no selection are omitted,
 * so an untouched board describes nothing.
 *
 * @param {object} filters - Trends board filter state
 * @returns {Array<{accessor: string, label: string, value: string}>}
 */
export const describeTrendFilters = (filters) => {
  // Only what the user actually changed. The shared slice ships defaults of its
  // own — callDuration is '1' — and treating those as choices would both
  // describe a scope nobody asked for and turn every untouched board into a
  // filtered request carrying a one-minute floor.
  const defaults = callSearchInitialState.filters
  const changed = (accessor) => !isEqual(filters[accessor], defaults[accessor])
  const countLabel = (values, noun) => {
    if (isEmpty(values)) {
      return ''
    }
    return values.length === 1 ? values[0].label : `${values.length} ${noun}`
  }

  const durationLabel = minCallDurationOptions.find(
    (option) => String(option.value) === String(filters.callDuration)
  )?.label

  const eventCount = EVENT_SECTIONS.reduce(
    (total, section) => total + (filters[section]?.selected?.length ?? 0),
    0
  )

  return [
    { accessor: 'agents', label: 'Agents', value: countLabel(filters.agents, 'agents') },
    { accessor: 'tags', label: 'Tags', value: countLabel(filters.tags, 'tags') },
    {
      accessor: 'playbooks',
      label: 'Playbooks',
      value: countLabel(filters.playbooks, 'playbooks'),
    },
    {
      accessor: 'dispositions',
      label: 'Dispositions',
      value: countLabel(filters.dispositions.selected, 'dispositions'),
    },
    {
      accessor: 'scorecards',
      label: 'Scorecards',
      value: countLabel(filters.scorecards, 'scorecards'),
    },
    {
      accessor: 'endingSentiment',
      label: 'Sentiment',
      value: countLabel(filters.endingSentiment, 'sentiments'),
    },
    {
      accessor: 'attachments',
      label: 'Attachments',
      value: countLabel(filters.attachments, 'types'),
    },
    {
      accessor: 'voipCampaigns',
      label: 'Campaign',
      value: countLabel(filters.voipCampaigns, 'campaigns'),
    },
    { accessor: 'direction', label: 'Direction', value: filters.direction },
    { accessor: 'isWin', label: 'Wins', value: filters.isWin === 'true' ? 'Only wins' : '' },
    { accessor: 'callDuration', label: 'Call Duration', value: durationLabel },
    {
      accessor: 'events',
      label: 'Events',
      value: eventCount > 0 ? `${eventCount} selected` : '',
      // spread over four keys, so it can't use the single-accessor comparison
      alwaysCompare: true,
    },
  ]
    .filter((filter) => filter.alwaysCompare || changed(filter.accessor))
    .filter((filter) => !isEmpty(filter.value))
}

/**
 * True when the board is narrowed by something the user chose.
 *
 * The window and organization are the board's scope, not a narrowing choice, so
 * they don't count — an untouched board issues the same unfiltered request it
 * always has.
 *
 * @param {object} filters - Trends board filter state
 */
export const hasActiveTrendFilters = (filters) => !isEmpty(describeTrendFilters(filters))
